import { Clock, ShieldAlert } from "lucide-react";
import { ImpactLedgerMark } from "@/shell/ImpactLedgerLogo";

/**
 * Full-screen gate shown to a contractor whose organisation has not yet been
 * approved by oversight (spec §9). The demo-mode counterpart of the backend
 * login block: a contractor persona whose seed org is `awaiting_gate`
 * (pending) or `suspended` (rejected) sees this instead of the workspace.
 * Calm and credible per §13.2 — no alarming colour, just a clear status.
 */
export function PendingApprovalScreen({
  variant,
  orgName,
  oversightName,
}: {
  variant: "pending" | "rejected";
  orgName: string;
  oversightName: string;
}) {
  const pending = variant === "pending";
  const Icon = pending ? Clock : ShieldAlert;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-6 py-16 text-center">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <ImpactLedgerMark size={32} />
        </div>
        <div className="rounded-xl border border-line bg-card p-8 shadow-sm">
          <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-subtle">
            <Icon className="h-6 w-6 text-mute" aria-hidden />
          </div>
          <h1 className="text-lg font-semibold tracking-tight text-ink">
            {pending ? "Onboarding submitted" : "Onboarding not approved"}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-mute">
            {pending ? (
              <>
                {oversightName} is reviewing <span className="font-medium text-ink">{orgName}</span>'s
                onboarding. You'll be able to access your workspace once it's approved.
              </>
            ) : (
              <>
                <span className="font-medium text-ink">{orgName}</span>'s onboarding wasn't approved.
                Please check your email for the reason, or contact {oversightName} to resolve it.
              </>
            )}
          </p>
          <p className="mt-6 border-t border-line pt-5 text-xs text-faint">
            {pending
              ? "Nothing more to do for now — we'll notify you by email when a decision is made."
              : "Once the issue is resolved, oversight can review your onboarding again."}
          </p>
        </div>
      </div>
    </div>
  );
}
